import { computed, watch } from 'vue';
import type { Router } from 'vue-router';

import { isCrosslistingBundle, resolveListingCategorySelection } from '@/helpers/listingUtils';
import { isSharedApartmentListing } from '@/helpers/sharedApartment';
import { setRumViewContext, type RumViewContext } from '@/services/datadog/DatadogService';
import { useInsertionStore } from '@/stores/insertion';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import { getBundleSegment } from '@/views/BundlesPage/utils/pricingBundle';

const lower = (value: string | undefined) => value?.toLowerCase();

/**
 * Keeps the Datadog RUM view context in sync with the stores. Custom actions and errors raised
 * during a view inherit it.
 * Call once after Pinia is installed. View name, device, service and feature flags are already
 * captured by RUM itself, so they are intentionally not repeated here.
 */
export function useRumContextTracking(router: Router): void {
  // Only SSG prerenders (landing pages) run on the server, and RUM is browser-only.
  if (import.meta.env.SSR) return;

  const userStore = useUserStore();
  const insertionStore = useInsertionStore();
  const paymentStore = usePaymentStore();
  const route = router.currentRoute;

  const viewContext = computed<RumViewContext>(() => {
    const { insertion } = insertionStore;
    // Insertion state must not leak onto views outside the insertion funnel (landing, My Ads).
    if (!route.value.params.id || !insertion) return {};

    const bundle = insertion.selectedBundle;
    return {
      // userType defaults to TENANT until the NIF API responds, so wait for the real value.
      user_type: userStore.isUserLoaded ? lower(userStore.userType) : undefined,
      canton: insertion.listing?.address?.region,
      offer_type: lower(insertion.listing?.offerType),
      category_group: lower(
        resolveListingCategorySelection(insertionStore.listingCategories)?.topCategory,
      ),
      is_shared_apartment: isSharedApartmentListing(insertionStore.listingCategories),
      insertion_status: lower(insertion.status),
      bundle_tier: bundle?.tier,
      // A bundle saved mid-flow can still lack an id.
      bundle_segment: bundle?.id ? getBundleSegment(bundle.id) : undefined,
      bundle_period_days: bundle?.period,
      crosslisting_selected: bundle?.id ? isCrosslistingBundle(bundle.id) : undefined,
      payment_type: lower(paymentStore.getPaymentType(insertion.id)),
    };
  });

  // startView() resets the view context on every navigation, so also re-apply when the path changes.
  watch([viewContext, () => route.value.fullPath], ([context]) => setRumViewContext(context), {
    immediate: true,
    flush: 'post',
  });
}
